import React from "react";
import { useNavigate } from "react-router-dom";
import { Badge } from "./ui/badge";
import { Button } from "./ui/button";
import { Bookmark, BookmarkCheck, Clock, Coins, Ratio, Droplet, Captions } from "lucide-react";

const PLATFORM_COLORS = {
  whop: "bg-indigo-500/15 text-indigo-300 border-indigo-500/30",
  clipping: "bg-pink-500/15 text-pink-300 border-pink-500/30",
  youtube: "bg-red-500/15 text-red-300 border-red-500/30",
  generic: "bg-zinc-500/15 text-zinc-300 border-zinc-500/30",
};

const STATUS = {
  active: { label: "Active", cls: "bg-emerald-500/15 text-emerald-400 border-emerald-500/30" },
  ending_soon: { label: "Ending Soon", cls: "bg-amber-500/15 text-amber-400 border-amber-500/30" },
  closed: { label: "Closed", cls: "bg-zinc-600/20 text-zinc-400 border-zinc-600/30" },
};

export function CampaignCard({ campaign, onToggleSave }) {
  const navigate = useNavigate();
  const req = campaign.requirements || {};
  const status = STATUS[campaign.status] || STATUS.active;
  const platCls = PLATFORM_COLORS[campaign.platform_id] || PLATFORM_COLORS.generic;

  const reqBadges = [];
  if (req.duration_exact) reqBadges.push({ icon: Clock, label: `${req.duration_exact}s` });
  else if (req.duration_min || req.duration_max)
    reqBadges.push({ icon: Clock, label: `${req.duration_min || 0}-${req.duration_max || "?"}s` });
  if (req.aspect_ratio) reqBadges.push({ icon: Ratio, label: req.aspect_ratio });
  if (req.watermark_required) reqBadges.push({ icon: Droplet, label: "Watermark" });
  if (req.caption_required) reqBadges.push({ icon: Captions, label: "Captions" });

  return (
    <div
      className="bg-[#141419] border border-white/10 rounded-2xl p-5 flex flex-col transition-all duration-200 hover:border-amber-500/40 hover:-translate-y-1 hover:shadow-xl hover:shadow-amber-500/5 group"
      data-testid={`campaign-card-${campaign.id}`}
    >
      <div className="flex items-center justify-between mb-3">
        <Badge className={`${platCls} border font-mono text-[10px] uppercase tracking-wider`}>
          {campaign.platform_name}
        </Badge>
        <div className="flex items-center gap-2">
          <Badge className={`${status.cls} border text-[10px]`}>{status.label}</Badge>
          <button
            onClick={(e) => { e.stopPropagation(); onToggleSave && onToggleSave(campaign); }}
            data-testid={`save-toggle-${campaign.id}`}
            className="text-zinc-400 hover:text-amber-400 transition-colors"
            title={campaign.is_saved ? "Remove from saved" : "Save campaign"}
          >
            {campaign.is_saved ? (
              <BookmarkCheck className="w-5 h-5 text-amber-400" />
            ) : (
              <Bookmark className="w-5 h-5" />
            )}
          </button>
        </div>
      </div>

      <h3 className="font-display font-bold text-lg leading-tight mb-1">{campaign.name}</h3>
      {campaign.brand && <p className="text-sm text-zinc-500 mb-3">{campaign.brand}</p>}

      <div className="flex flex-wrap gap-3 text-xs text-zinc-400 mb-4">
        {campaign.payout && (
          <span className="flex items-center gap-1 text-emerald-400">
            <Coins className="w-3.5 h-3.5" /> {campaign.payout}
          </span>
        )}
        {campaign.deadline && (
          <span className="flex items-center gap-1">
            <Clock className="w-3.5 h-3.5" /> {campaign.deadline}
          </span>
        )}
        <span className="text-zinc-500">{campaign.category}</span>
      </div>

      {reqBadges.length > 0 && (
        <div className="flex flex-wrap gap-1.5 mb-4">
          {reqBadges.map((b, i) => (
            <span
              key={i}
              className="flex items-center gap-1 px-2 py-1 rounded-md bg-white/5 text-[11px] text-zinc-300"
            >
              <b.icon className="w-3 h-3 text-amber-400" /> {b.label}
            </span>
          ))}
        </div>
      )}

      <div className="mt-auto pt-2">
        <Button
          onClick={() => navigate(`/campaigns/${campaign.id}`)}
          data-testid={`view-campaign-${campaign.id}`}
          className="w-full bg-white/5 hover:bg-amber-500 hover:text-zinc-950 border border-white/10 hover:border-amber-500 transition-all font-semibold"
        >
          View Campaign
        </Button>
      </div>
    </div>
  );
}
